import React, { useEffect, useRef, useCallback, useState } from "react";
import ReactDOM from "react-dom";
import "./CustomModal.css";

export default function CustomModal({
  open,
  title,
  children,
  onClose,
  footer,
  size = "lg",
  closeOnOverlay = true,
  preventCloseOnEsc = false,
  initialFocusRef,
  containerClassName = "",
  /** ★ 追加: 親が閉じてくれなくても自分で閉じるフェイルセーフ */
  forceUnmountOnClose = true,
  /** ★ 追加: デバッグログを出す（必要なければ false のまま） */
  debug = false,
}) {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const activeElBefore = useRef(null);

  /** ★ 追加: フェイルセーフ用の内部 state */
  const [dismissed, setDismissed] = useState(false);

  /** ★ 追加: open が変わったら内部 dismissed を同期 */
  useEffect(() => {
    if (open) setDismissed(false);
  }, [open]);

  // body スクロールロック
  useEffect(() => {
    if (!open || dismissed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, dismissed]);

  // 直前フォーカス保存＆復帰
  useEffect(() => {
    if (open && !dismissed) activeElBefore.current = document.activeElement;
    return () => {
      if (activeElBefore.current && activeElBefore.current.focus) {
        activeElBefore.current.focus();
      }
    };
  }, [open, dismissed]);

  // 初期フォーカス
  useEffect(() => {
    if (!open || dismissed) return;
    const node =
      initialFocusRef?.current ||
      contentRef.current?.querySelector("[data-autofocus]") ||
      contentRef.current;
    node && node.focus?.();
  }, [open, dismissed, initialFocusRef]);

  /** ★ 追加: 閉じる共通関数（親とフェイルセーフ両対応） */
  const requestClose = useCallback((source = "unknown") => {
    debug && console.log(`[CustomModal] close from: ${source}`);
    try { onClose?.(); } catch (e) { console.error(e); }
    if (forceUnmountOnClose) {
      // 親が閉じなくても自分でアンマウント
      setDismissed(true);
    }
  }, [onClose, forceUnmountOnClose, debug]);

  // ESC と Tabtrap
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape" && !preventCloseOnEsc) {
      e.stopPropagation();
      requestClose("escape");
    }
    if (e.key === "Tab") {
      const focusables = contentRef.current?.querySelectorAll(
        'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusables || []).filter(el => !el.hasAttribute("disabled"));
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [preventCloseOnEsc, requestClose]);

  /** ★ 変更: dismissed のときは描画しない（強制クローズ） */
  if (!open || dismissed) return null;

  return ReactDOM.createPortal(
    <div
      className="cm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : "modal"}
      ref={overlayRef}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (!closeOnOverlay) return;
        if (e.target === overlayRef.current) requestClose("overlay");
      }}
    >
      <div
        className={`cm-container cm-size-${size} ${containerClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="cm-header">
          <h3 className="cm-title">{title}</h3>
          <button
            type="button"
            className="cm-close"
            aria-label="閉じる"
            onMouseDown={(e) => { e.stopPropagation(); requestClose("close-mousedown"); }}
            onClick={(e) => { e.stopPropagation(); requestClose("close-click"); }}
          >
            ×
          </button>
        </div>

        {/* コンテンツ */}
        <div className="cm-content" ref={contentRef} tabIndex={-1}>
          {children}
        </div>

        {/* フッター */}
        {footer ? <div className="cm-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}